// Microsoft Graph Service - Updated for Supabase Edge Functions
// Now uses secure backend authentication with Sites.Selected permissions

import { supabase } from '../lib/supabase';

const GRAPH_API_BASE = 'https://graph.microsoft.com/v1.0';

class MicrosoftGraphService {
  constructor() {
    this.edgeFunctionUrl = null;
  }

  async initialize() {
    // Get Supabase Edge Function URL
    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
    this.edgeFunctionUrl = `${supabaseUrl}/functions/v1/microsoft-graph-auth`;
  }

  /**
   * Call Microsoft Graph API via Supabase Edge Function
   * This uses secure client credentials authentication
   */
  async callGraphAPI(endpoint, method = 'GET', body = null) {
    if (!this.edgeFunctionUrl) {
      await this.initialize();
    }

    try {
      console.log(`[GRAPH_SERVICE] Calling Edge Function for ${method} ${endpoint}`);

      // Get current user session for authentication
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      if (sessionError || !session) {
        console.error('[GRAPH_SERVICE] Session error:', sessionError);
        throw new Error('User not authenticated. Please log in again.');
      }

      console.log('[GRAPH_SERVICE] User session valid, calling Edge Function...');
      console.log('[GRAPH_SERVICE] Edge Function URL:', this.edgeFunctionUrl);

      // Call Edge Function with user's session token
      let response;
      try {
        response = await fetch(this.edgeFunctionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            endpoint,
            method,
            body,
          }),
        });
      } catch (fetchError) {
        console.error('[GRAPH_SERVICE] Fetch failed:', fetchError);
        
        // Detect specific fetch errors with improved user guidance
        if (fetchError?.message?.toLowerCase()?.includes('failed to fetch')) {
          throw new Error(
            '⚠️ SharePoint Integration Not Available\n\n' + 'The Microsoft Graph Edge Function required for SharePoint operations is not currently deployed or accessible.\n\n'+ '🔧 To enable SharePoint integration, an administrator must:\n'+ '1. Deploy the Edge Function using: supabase functions deploy microsoft-graph-auth\n'+ '2. Configure Microsoft Graph credentials in System Settings\n'+ '3. Verify the function is running in Supabase Dashboard\n\n'+ '💡 Note: Documents stored in Supabase storage can still be managed without this integration.\n\n'+ 'For detailed setup instructions, see SUPABASE_EDGE_FUNCTIONS_SETUP.md'
          );
        }
        
        // Re-throw other fetch errors
        throw new Error(`Network error: ${fetchError?.message}`);
      }

      console.log('[GRAPH_SERVICE] Edge Function response status:', response?.status);
      console.log('[GRAPH_SERVICE] Edge Function response headers:', Object.fromEntries(response?.headers?.entries() || []));

      // Check if response indicates the function doesn't exist or isn't deployed
      if (response?.status === 404) {
        console.error('[GRAPH_SERVICE] Edge Function not found (404)');
        throw new Error(
          '⚠️ SharePoint Integration Not Deployed\n\n' + 'The Microsoft Graph Edge Function is not deployed to this Supabase project.\n\n'+ '🔧 To enable SharePoint integration, run:\n'+ 'supabase functions deploy microsoft-graph-auth\n\n'+ '💡 Until deployment is complete, only Supabase storage operations will work.\n\n'+ 'For detailed deployment instructions, see SUPABASE_EDGE_FUNCTIONS_SETUP.md'
        );
      }

      // Get response text first to handle both JSON and non-JSON responses
      const responseText = await response?.text();
      console.log('[GRAPH_SERVICE] Response text length:', responseText?.length);
      console.log('[GRAPH_SERVICE] Response preview:', responseText?.substring(0, 200));

      // Check if response is empty
      if (!responseText || responseText?.length === 0) {
        console.error('[GRAPH_SERVICE] Empty response from Edge Function');
        throw new Error(
          'Empty response from Microsoft Graph Edge Function. This usually means:\n' + '1. The Edge Function is not deployed yet - Deploy using: supabase functions deploy microsoft-graph-auth\n'+ '2. The Edge Function crashed before responding - Check Edge Function logs in Supabase Dashboard\n'+ '3. Network/CORS issue - Verify SUPABASE_URL in .env matches your project\n\n'+ 'Check Supabase Dashboard → Edge Functions → microsoft-graph-auth for logs and deployment status'
        );
      }

      // Try to parse as JSON
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('[GRAPH_SERVICE] Failed to parse response as JSON:', parseError);
        console.error('[GRAPH_SERVICE] Response text:', responseText);
        throw new Error(
          `Invalid JSON response from Edge Function. This usually indicates a deployment or runtime error.\n\n` +
          `Response preview: ${responseText.substring(0, 200)}\n\n` +
          `Check Edge Function logs in Supabase Dashboard for detailed error information.`
        );
      }

      // Check if the operation was successful (handle both successful and error responses)
      if (!response?.ok || !result?.success) {
        const errorMessage = result?.error || 'Microsoft Graph API request failed';
        console.error('[GRAPH_SERVICE] API error:', errorMessage);
        
        // Provide specific guidance based on error type
        if (errorMessage?.includes('credentials not configured') || errorMessage?.includes('Incomplete Microsoft Graph credentials')) {
          throw new Error(
            'Microsoft Graph credentials are not configured.\n\n' + 'To configure credentials:\n'+ '1. Go to System Settings → Integrations tab\n'+ '2. Find the "Microsoft Graph Configuration" section\n'+ '3. Enter your Azure App Registration credentials:\n'+ '   • Client ID\n'+ '   • Client Secret\n'+ '   • Tenant ID\n\n'+ 'Get these credentials from Azure Portal → App Registrations → [Your App]'
          );
        }
        
        // Throw the actual error message from the Edge Function
        throw new Error(errorMessage);
      }

      console.log('[GRAPH_SERVICE] Request successful');
      return result?.data;
    } catch (error) {
      console.error('[GRAPH_SERVICE] Error calling Microsoft Graph API:', error);
      
      // Only catch network errors (actual fetch failures), not API errors
      if (error?.message?.includes('fetch') && !error?.message?.includes('credentials')) {
        throw new Error(
          'Network error connecting to Microsoft Graph Edge Function.\n\n' + 'Please check:\n'+ '1. Your internet connection\n'+ '2. VITE_SUPABASE_URL is correctly set in .env file\n'+ '3. Edge Function is deployed: supabase functions deploy microsoft-graph-auth\n'+ '4. Edge Function has proper CORS configuration\n\n'+ 'Check Supabase Dashboard → Edge Functions for deployment status'
        );
      }
      
      // Re-throw all other errors (including credentials errors) as-is
      throw error;
    }
  }

  /**
   * Get SharePoint sites - Now using Edge Function
   */
  async getSharePointSites() {
    try {
      console.log('[GRAPH_SERVICE] Calling Edge Function to get SharePoint sites...');
      
      // With Sites.Selected, we can't search all sites
      // Use the specific site ID from PowerShell: psmnv.sharepoint.com,72068bcc-d4f7-4fcd-a137-1eb0af46e58d,692293d6-b4e4-48f3-9e18-6f7db3c4dcba
      // Or get it from environment/config
      const siteId = import.meta.env?.VITE_SHAREPOINT_SITE_ID || 
                     'psmnv.sharepoint.com,72068bcc-d4f7-4fcd-a137-1eb0af46e58d,692293d6-b4e4-48f3-9e18-6f7db3c4dcba';
      
      // Get the specific site (works with Sites.Selected)
      const site = await this.callGraphAPI(`/sites/${encodeURIComponent(siteId)}`);
      console.log('[GRAPH_SERVICE] Edge Function returned site:', site);
      
      // Return as array format for compatibility
      return { value: [site] };
    } catch (error) {
      console.error('[GRAPH_SERVICE] Error fetching SharePoint sites:', error);
      
      // If specific site fails, try root site as fallback
      if (error?.message?.includes('access denied') || error?.message?.includes('not found')) {
        try {
          console.log('[GRAPH_SERVICE] Trying root site as fallback...');
          const rootSite = await this.callGraphAPI('/sites/root');
          return { value: [rootSite] };
        } catch (rootError) {
          console.error('[GRAPH_SERVICE] Root site also failed:', rootError);
          throw new Error(
            'SharePoint site access denied. ' + 'With Sites.Selected permission, you must use a specific site ID. '+ 'Please configure VITE_SHAREPOINT_SITE_ID in your .env file or ensure the site is registered with your app.'
          );
        }
      }
      throw error;
    }
  }

  /**
   * Get document libraries for a specific SharePoint site
   */
  async getSiteDocumentLibraries(siteId) {
    if (!siteId) {
      throw new Error('Site ID is required');
    }

    try {
      return await this.callGraphAPI(`/sites/${siteId}/drives`);
    } catch (error) {
      console.error('Error fetching document libraries:', error);
      throw error;
    }
  }

  /**
   * List documents in a SharePoint library
   */
  async listDocuments(siteId, driveId, folderPath = '') {
    if (!siteId || !driveId) {
      throw new Error('Site ID and Drive ID are required');
    }

    try {
      const endpoint = folderPath
        ? `/sites/${siteId}/drives/${driveId}/root:/${folderPath}:/children`
        : `/sites/${siteId}/drives/${driveId}/root/children`;

      let response = await this.callGraphAPI(endpoint);
      return response?.value || [];
    } catch (error) {
      console.error('Error listing documents:', error);
      throw error;
    }
  }

  /**
   * Upload document to SharePoint via Edge Function
   */
  async uploadDocument(siteId, driveId, file, fileName, folderPath = '') {
    if (!siteId || !driveId) {
      throw new Error('Site ID and Drive ID are required');
    }

    try {
      // Convert file to base64 for transmission
      const base64 = await this.fileToBase64(file);
      
      const uploadPath = folderPath ? `${folderPath}/${fileName}` : fileName;
      const endpoint = `/sites/${siteId}/drives/${driveId}/root:/${uploadPath}:/content`;

      return await this.callGraphAPI(endpoint, 'PUT', {
        content: base64,
        contentType: file?.type || 'application/octet-stream',
      });
    } catch (error) {
      console.error('Error uploading to SharePoint:', error);
      throw error;
    }
  }

  /**
   * Create folder in SharePoint
   */
  async createFolder(siteId, driveId, folderName, parentPath = '') {
    if (!siteId || !driveId) {
      throw new Error('Site ID and Drive ID are required');
    }

    try {
      const endpoint = parentPath
        ? `/sites/${siteId}/drives/${driveId}/root:/${parentPath}:/children`
        : `/sites/${siteId}/drives/${driveId}/root/children`;

      return await this.callGraphAPI(endpoint, 'POST', {
        name: folderName,
        folder: {},
        '@microsoft.graph.conflictBehavior': 'rename',
      });
    } catch (error) {
      console.error('Error creating folder:', error);
      throw error;
    }
  }

  /**
   * Delete document from SharePoint
   */
  async deleteDocument(siteId, driveId, itemId) {
    try {
      console.log('[GRAPH_SERVICE] Deleting document:', {
        siteId,
        driveId,
        itemId
      });

      const endpoint = `/sites/${siteId}/drives/${driveId}/items/${itemId}`;
      let result = await this.callGraphAPI(endpoint, 'DELETE');
      
      console.log('[GRAPH_SERVICE] Document deleted successfully');
      return result;
    } catch (error) {
      console.error('[GRAPH_SERVICE] Error deleting document:', error);
      
      // Parse error response to provide detailed information
      if (error?.message?.includes('404') || error?.message?.toLowerCase()?.includes('itemnotfound')) {
        // Document not found in SharePoint
        throw new Error('Document not found in SharePoint (404)');
      } else if (error?.message?.includes('Edge Function not found')) {
        throw new Error('SharePoint Integration Not Deployed: Microsoft Graph Edge Function is not available');
      }
      
      // Re-throw original error with preserved information
      throw error;
    }
  }

  /**
   * Get document edit URL
   */
  async getDocumentEditUrl(siteId, driveId, itemId) {
    if (!siteId || !driveId || !itemId) {
      throw new Error('Site ID, Drive ID, and Item ID are required');
    }

    try {
      const endpoint = `/sites/${siteId}/drives/${driveId}/items/${itemId}`;
      const item = await this.callGraphAPI(endpoint);
      return item?.webUrl;
    } catch (error) {
      console.error('Error getting edit URL:', error);
      throw error;
    }
  }

  /**
   * Search for documents in SharePoint
   */
  async searchDocuments(siteId, driveId, query) {
    if (!siteId || !driveId) {
      throw new Error('Site ID and Drive ID are required');
    }

    try {
      const endpoint = `/sites/${siteId}/drives/${driveId}/root/search(q='${encodeURIComponent(query)}')`;
      let response = await this.callGraphAPI(endpoint);
      return response?.value || [];
    } catch (error) {
      console.error('Error searching documents:', error);
      throw error;
    }
  }

  /**
   * Helper: Convert file to base64
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result?.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Get Office Online URL for viewing/editing
   * This still works without authentication for public URLs
   */
  async getOfficeOnlineUrl(document) {
    const extension = document?.name?.split('.')?.pop()?.toLowerCase();
    let app = 'view';
    
    switch (extension) {
      case 'docx':
      case 'doc':
        app = 'word';
        break;
      case 'xlsx': case'xls':
        app = 'excel';
        break;
      case 'pptx': case'ppt':
        app = 'powerpoint';
        break;
      default:
        app = 'view';
    }

    const documentUrl = encodeURIComponent(document?.storage_path || document?.url);
    return `https://view.officeapps.live.com/${app}/view.aspx?src=${documentUrl}`;
  }
}

export default new MicrosoftGraphService();